import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchInterestZoneSavedMap,
  fetchInterestZoneSavedMaps,
} from '../queries/interestZoneSavedMaps.queries'
import {
  addZonesToSavedMap,
  createInterestZoneSavedMap,
  deleteInterestZoneSavedMap,
  removeZoneFromSavedMap,
  updateInterestZoneSavedMap,
} from '../mutations/interestZoneSavedMaps.mutations'

const LIST_KEY = ['interest-zone-saved-maps']
const detailKey = (id: number) => ['interest-zone-saved-maps', id]

export function useInterestZoneSavedMaps() {
  return useQuery({ queryKey: LIST_KEY, queryFn: fetchInterestZoneSavedMaps })
}

export function useInterestZoneSavedMap(id: number | null) {
  return useQuery({
    queryKey: detailKey(id ?? 0),
    queryFn: () => fetchInterestZoneSavedMap(id!),
    enabled: id != null,
  })
}

export function useCreateInterestZoneSavedMap() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createInterestZoneSavedMap,
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  })
}

export function useUpdateInterestZoneSavedMap() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; name?: string; description?: string | null }) =>
      updateInterestZoneSavedMap(vars.id, { name: vars.name, description: vars.description }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
      qc.invalidateQueries({ queryKey: detailKey(vars.id) })
    },
  })
}

export function useDeleteInterestZoneSavedMap() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteInterestZoneSavedMap,
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  })
}

export function useAddZonesToSavedMap() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; runKey: string; zoneIds: number[] }) =>
      addZonesToSavedMap(vars.id, { runKey: vars.runKey, zoneIds: vars.zoneIds }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
      qc.invalidateQueries({ queryKey: detailKey(vars.id) })
    },
  })
}

export function useRemoveZoneFromSavedMap() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; zoneId: number }) =>
      removeZoneFromSavedMap(vars.id, vars.zoneId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
      qc.invalidateQueries({ queryKey: detailKey(vars.id) })
    },
  })
}
